import { AppInsightsContext, AppInsightsErrorBoundary, } from '@microsoft/applicationinsights-react-js';
import { generateW3CId } from '@microsoft/applicationinsights-core-js';
import { useMsal } from '@azure/msal-react';
import { type ReactNode, useEffect } from 'react';
import type { Router } from '@tanstack/react-router';
import { appInsights, reactPlugin } from './appInsights';

export interface ApplicationInsightsProviderProps {
  router: Router<any, any, any>;
  children: ReactNode;
}

function ErrorFallback() {
  return (
    <div style={{maxWidth: 600, margin: '3rem auto', textAlign: 'center'}}>
      <h2 style={{fontSize: '1.5rem', marginBottom: '0.5rem'}}>Something went wrong</h2>
      <p>Please refresh the page.</p>
    </div>
  );
}

export function ApplicationInsightsProvider({
                                              router,
                                              children,
                                            }: ApplicationInsightsProviderProps) {
  const {accounts} = useMsal();

  // Track TanStack Router navigation events
  useEffect(() => {
    if (!appInsights) return;

    const ai = appInsights;

    // Reset trace ID BEFORE loaders run so all queries on the new page
    // are correlated to the new page view, not the previous one
    const unsubBeforeLoad = router.subscribe('onBeforeLoad', (event) => {
      if (event.pathChanged) {
        ai.context.telemetryTrace.traceID = generateW3CId();
        ai.context.telemetryTrace.parentID = undefined;
      }
    });

    // Track the page view after navigation fully completes
    const unsubResolved = router.subscribe('onResolved', (event) => {
      if (event.pathChanged) {
        ai.trackPageView({
          name: event.toLocation.pathname,
          uri: event.toLocation.href,
        });
      }
    });

    return () => {
      unsubBeforeLoad();
      unsubResolved();
    };
  }, [router]);

  // Set authenticated user context when user logs in/out
  useEffect(() => {
    if (!appInsights) return;

    const account = accounts[0];
    if (account?.localAccountId) {
      // localAccountId is the Entra Object ID (oid claim)
      // This sets user_AuthenticatedId in Application Insights
      appInsights.setAuthenticatedUserContext(account.localAccountId, undefined, true);
    } else {
      appInsights.clearAuthenticatedUserContext();
    }
  }, [accounts]);

  if (!reactPlugin) {
    return <>{children}</>;
  }

  return (
    <AppInsightsErrorBoundary onError={ErrorFallback} appInsights={reactPlugin}>
      <AppInsightsContext.Provider value={reactPlugin}>{children}</AppInsightsContext.Provider>
    </AppInsightsErrorBoundary>
  );
}
